"use client"

import TextField from "@mui/material/TextField"
import {
  Controller,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form"
import { colors } from "../../../theme/colors"

/** Pill-shaped outlined input styling shared by every form field. */
export const pillFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "9999px",
    height: 48,
    // EGDE-224: desktop columns are NARROWER than the single mobile column, so
    // the field/label font must not grow past mobile or long Hebrew labels get
    // clipped at 1280px. 16px desktop keeps every label fully readable.
    fontSize: { xs: 18, lg: 16 },
    "& fieldset": {
      borderColor: "#d0d0d0",
    },
  },
  // Browsers force `type="tel"` (and similar) inputs to LTR, which left-aligns
  // the value in an RTL form while every other field aligns right. Inherit the
  // page direction so alignment follows the locale (right in he/ar, left in en/ru).
  "& .MuiOutlinedInput-input": {
    direction: "inherit",
    textAlign: "start",
  },
  "& .MuiInputLabel-root": {
    fontSize: { xs: 18, lg: 16 },
    // EGDE-446: MUI's translate(14px, 9px) centres the label in its default
    // 40px small input — inside our 48px pill it sat above centre (9px above
    // vs 15px below the text on mobile). Nudge the resting label to centre;
    // the floated (shrunk) label keeps its stock position.
    "&:not(.MuiInputLabel-shrink)": {
      top: { xs: "3px", lg: "1px" },
    },
    "&.MuiInputLabel-shrink": {
      backgroundColor: "#fff",
      paddingInline: "4px",
    },
  },
  // Shared by every field that renders a MUI label.
  // EGDE-528: the required asterisk is brand orange at rest (per Figma) and
  // only turns red together with the label when the field is in error.
  "& .MuiInputLabel-asterisk": {
    color: colors.primitives.primary500,
  },
  "& .MuiInputLabel-root.Mui-error .MuiInputLabel-asterisk": {
    color: colors.primitives.error500,
  },
} as const

/** Multiline variant — relaxes the fixed pill height so the textarea grows. */
export const areaFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "24px",
    fontSize: { xs: 18, lg: 20 },
    // EGDE-224: start at the same 48px height as the pill fields so a single-row
    // description aligns with its sibling field instead of rendering shorter.
    // It still grows with content up to maxRows.
    minHeight: 48,
    "& fieldset": {
      borderColor: "#d0d0d0",
    },
  },
  "& .MuiOutlinedInput-input": {
    direction: "inherit",
    textAlign: "start",
  },
  "& .MuiInputLabel-root": {
    fontSize: { xs: 18, lg: 16 },
    // EGDE-446: same centring nudge as pillFieldSx — the single-row state of
    // the 48px textarea otherwise shows its label above centre.
    "&:not(.MuiInputLabel-shrink)": {
      top: { xs: "3px", lg: "1px" },
    },
    "&.MuiInputLabel-shrink": {
      backgroundColor: "#fff",
      paddingInline: "4px",
    },
  },
  // EGDE-528: the required asterisk is brand orange at rest (per Figma) and
  // only turns red together with the label when the field is in error.
  "& .MuiInputLabel-asterisk": {
    color: colors.primitives.primary500,
  },
  "& .MuiInputLabel-root.Mui-error .MuiInputLabel-asterisk": {
    color: colors.primitives.error500,
  },
} as const

interface FormTextFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  label: string
  required?: boolean
  type?: string
  autoComplete?: string
  inputMode?: "text" | "numeric" | "tel" | "email"
  multiline?: boolean
  rows?: number
  maxLength?: number
  error?: string
}

export default function FormTextField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  type,
  autoComplete,
  inputMode,
  multiline,
  rows,
  maxLength,
  error,
}: FormTextFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          value={field.value ?? ""}
          label={label}
          required={required}
          type={type}
          multiline={multiline}
          minRows={multiline ? (rows ?? 4) : undefined}
          maxRows={multiline ? 8 : undefined}
          error={!!error}
          helperText={error}
          fullWidth
          variant="outlined"
          size="small"
          autoComplete={autoComplete}
          inputProps={{
            ...(maxLength ? { maxLength } : {}),
            ...(inputMode ? { inputMode } : {}),
          }}
          sx={multiline ? areaFieldSx : pillFieldSx}
        />
      )}
    />
  )
}
